import { useEffect, useState } from 'react'
import { Truck } from 'lucide-react'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'

import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    useActiveTrip,
    useDriverActiveTrip,
} from '@/hooks/use-delivery-trips-query'
import { useAuth } from '@/hooks/use-auth'
import { useStartDelivery } from '@/hooks/use-sales-order-query'
import { useAvailableVehicles } from '@/hooks/use-vehicles-query'
import { m } from '@/lib/paraglide/messages'

interface Props {
    open: boolean
    onOpenChange: (open: boolean) => void
    orderId: string
    /**
     * Tài xế được gán của đơn. Khi người bấm không phải người này (văn phòng
     * bấm hộ bác tài không có điện thoại), popup phải soi chuyến của TÀI XẾ chứ
     * không phải của người bấm — chuyến giao thuộc về tài xế.
     */
    driverId?: string
}

export function StartDeliveryDialog({
    open,
    onOpenChange,
    orderId,
    driverId,
}: Props) {
    const workflow = useWorkflowTracking('delivery_start', {
        active: open,
        attemptKey: orderId,
    })
    const { user } = useAuth()
    const onBehalf = Boolean(driverId && user?.id && driverId !== user.id)
    // Cả hai hook luôn được gọi (quy tắc hook), chỉ một cái bật `enabled`.
    const selfTrip = useActiveTrip(open && !onBehalf)
    const driverTrip = useDriverActiveTrip(driverId ?? '', open && onBehalf)
    const activeTrip = onBehalf ? driverTrip.data : selfTrip.data
    const tripLoading = onBehalf ? driverTrip.isLoading : selfTrip.isLoading
    const { data: vehicles = [], isLoading: vehiclesLoading } =
        useAvailableVehicles(open && !activeTrip)
    const startDelivery = useStartDelivery()

    const [selectedVehicleId, setSelectedVehicleId] = useState<string>('')
    useEffect(() => {
        if (!open) {
            setSelectedVehicleId('')
        }
    }, [open])

    const needsVehicle = !activeTrip
    const canSubmit =
        !tripLoading && (!needsVehicle || Boolean(selectedVehicleId))

    const handleSubmit = async () => {
        try {
            await workflow.run(
                () =>
                    startDelivery.mutateAsync({
                        id: orderId,
                        vehicle_id: needsVehicle
                            ? selectedVehicleId
                            : undefined,
                    }),
                (result) => result?.data?.id,
            )
            onOpenChange(false)
        } catch {
            // Lỗi từ API do useStartDelivery.onError hiển thị toast riêng.
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{m.trip_start_delivery_title()}</DialogTitle>
                </DialogHeader>

                {tripLoading ? (
                    <p className="text-sm text-muted-foreground">
                        {m.common_loading()}
                    </p>
                ) : activeTrip ? (
                    <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
                        <div className="flex items-center gap-2 font-medium">
                            <Truck className="size-4" />
                            {activeTrip.vehicle?.plate_number ?? ''} ·{' '}
                            {activeTrip.trip_code}
                        </div>
                        <p className="mt-1 text-muted-foreground">
                            {m.trip_picker_attached_to_existing()}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        <label className="text-sm font-medium">
                            {m.trip_picker_select_vehicle()} *
                        </label>
                        {vehiclesLoading ? (
                            <p className="text-sm text-muted-foreground">
                                {m.common_loading()}
                            </p>
                        ) : vehicles.length === 0 ? (
                            <p className="text-sm text-destructive">
                                {m.trip_picker_no_vehicles()}
                            </p>
                        ) : (
                            <Select
                                value={selectedVehicleId}
                                onValueChange={(value) => {
                                    workflow.interact('selection')
                                    workflow.step('selection_changed')
                                    setSelectedVehicleId(value)
                                }}
                            >
                                <SelectTrigger>
                                    <SelectValue
                                        placeholder={m.trip_picker_select_vehicle()}
                                    />
                                </SelectTrigger>
                                <SelectContent>
                                    {vehicles.map((v) => (
                                        <SelectItem key={v.id} value={v.id}>
                                            {v.plate_number} — {v.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    </div>
                )}

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                    >
                        {m.common_cancel()}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={!canSubmit || startDelivery.isPending}
                    >
                        {startDelivery.isPending
                            ? m.common_saving()
                            : m.trip_start_delivery_confirm()}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
